import React from 'react';
import { Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WaterIcon from '@mui/icons-material/Water';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import AirIcon from '@mui/icons-material/Air';
import SpeedIcon from '@mui/icons-material/Speed';
import CloudIcon from '@mui/icons-material/Cloud';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import useTranslation from '../../customHooks/translations';
import { useLanguageContext } from '../../contexts/LanguageContext';

function trendArrow(trend) {
  if (trend === 'rising') return '↗';
  if (trend === 'falling') return '↘';
  return '→';
}

function MetricRow({ icon, label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 0.8, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
      <Box sx={{ color: DEFAULT_THEME_COLOR, display: 'flex', alignItems: 'center', width: 28 }}>{icon}</Box>
      <Typography variant="body2" sx={{ flex: 1, opacity: 0.75, ml: 0.5 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Box>
  );
}

const WeatherDayCard = ({ day, isLoading }) => {
  const translation = useTranslation();
  const { language } = useLanguageContext();
  const locale = language === 'pl' ? 'pl-PL' : 'en-US';

  function formatDateHeader(dateStr) {
    const d = new Date(dateStr + 'T12:00:00Z');
    const today = new Date();
    const isToday =
      d.getUTCFullYear() === today.getUTCFullYear() &&
      d.getUTCMonth() === today.getUTCMonth() &&
      d.getUTCDate() === today.getUTCDate();
    const dateLabel = d.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
    if (isToday) return `${translation.weather.today} · ${dateLabel}`;
    const weekday = d.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' });
    return `${weekday} · ${dateLabel}`;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} sx={{ color: DEFAULT_THEME_COLOR }} />
      </Box>
    );
  }

  if (!day) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2, fontStyle: 'italic' }}>
        {translation.weather.noData}
      </Typography>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderColor: DEFAULT_THEME_COLOR, mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: DEFAULT_THEME_COLOR, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
          {formatDateHeader(day.date)}
        </Typography>
        <MetricRow icon={<ThermostatIcon fontSize="small" />} label={translation.weather.temperature} value={`${Math.round(day.airTempMinC)}° / ${Math.round(day.airTempMaxC)}°C`} />
        <MetricRow icon={<WaterIcon fontSize="small" />} label={translation.weather.water} value={day.waterTempC !== null ? `${day.waterTempC}°C` : '—'} />
        <MetricRow icon={<UmbrellaIcon fontSize="small" />} label={translation.weather.precipitation} value={`${day.precipMm24h.toFixed(1)} mm`} />
        <MetricRow icon={<AirIcon fontSize="small" />} label={translation.weather.wind} value={`${Math.round(day.windKmh)} km/h`} />
        <MetricRow icon={<SpeedIcon fontSize="small" />} label={translation.weather.pressure} value={`${Math.round(day.pressureHpa)} hPa ${trendArrow(day.pressureTrend)}`} />
        <MetricRow icon={<CloudIcon fontSize="small" />} label={translation.weather.cloudCover} value={`${Math.round(day.cloudCoverPct)}%`} />
      </CardContent>
    </Card>
  );
};

export default WeatherDayCard;
